import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import {
  type CreateEntryInput,
  createEntry,
  deleteEntry,
  type Entry,
  fetchMembers,
  LIMITS,
  type ProposalInput,
  queryKeys,
  type Track,
  updateEntry,
  type Visibility,
} from "./api";
import { DatePreview } from "./date-preview";
import { type EntryForm, entryBody, formFromEntry, formIssue, proposalBody } from "./entry-body";

const FIELD =
  "min-h-11 w-full rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground placeholder:text-[#8C96AA]";
const LABEL = "text-sm font-semibold";

/**
 * L'ecran d'un point (planche « Point ») : feuille laterale au bureau, plein ecran au telephone.
 * Creer, modifier, supprimer. Les dates se tapent en texte libre, avec l'apercu de ce que l'app a
 * compris ; sans « Quand ? », le point part dans le bac « a dater ».
 *
 * Avec `propose` (la vue d'un proche, ticket 7), la feuille sert a proposer un souvenir : ni
 * visibilite, ni suppression, ni apercu des dates (la lecture des dates est une route du
 * proprietaire ; le serveur lit le texte a l'envoi et dit s'il ne le comprend pas).
 */
export function EntrySheet({
  entry,
  tracks,
  focusWhen = false,
  initial,
  heading,
  submit: submitBody,
  propose,
  onSaved,
  onClose,
}: {
  /** Le point a modifier, ou `null` pour un nouveau point. */
  entry: Entry | null;
  tracks: readonly Track[];
  /** Ouvert par « Dater » : le focus va directement au champ « Quand ? ». */
  focusWhen?: boolean;
  /** Un nouveau point prerempli (une question : piste, type, « Quand ? »). */
  initial?: Partial<EntryForm>;
  /** Le titre de la feuille, a la place de « Nouveau point » ou « Modifier le point ». */
  heading?: string;
  /**
   * L'envoi, a la place de la creation ou de la modification (une reponse a une question). La
   * suppression n'est alors pas proposee.
   */
  submit?: (body: CreateEntryInput) => Promise<unknown>;
  /** L'envoi d'une proposition d'un proche, a la place de tout le reste. */
  propose?: (body: ProposalInput) => Promise<unknown>;
  /** Apres un enregistrement reussi, avant la fermeture. */
  onSaved?: () => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<EntryForm>(() => ({
    ...formFromEntry(entry, tracks[0]?.id ?? null),
    ...initial,
  }));
  const [issue, setIssue] = useState<string | null>(null);
  const [serverError, setServerError] = useState<ApiError | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const id = useId();
  const titleRef = useRef<HTMLInputElement>(null);
  const whenRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (focusWhen ? whenRef : titleRef).current?.focus();
  }, [focusWhen]);

  const set = <K extends keyof EntryForm>(key: K, value: EntryForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.frise });
    onSaved?.();
    onClose();
  };
  const fail = (error: unknown) =>
    setServerError(
      error instanceof ApiError
        ? error
        : new ApiError("network", "L'enregistrement n'a pas abouti. Réessaie.", 0),
    );

  const save = useMutation({
    mutationFn: () => {
      if (propose !== undefined) return propose(proposalBody(form));
      const body = entryBody(form);
      if (submitBody !== undefined) return submitBody(body);
      return entry === null ? createEntry(body) : updateEntry(entry.id, body);
    },
    onSuccess: done,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => deleteEntry((entry as Entry).id),
    onSuccess: done,
    onError: fail,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);
    const problem = formIssue(form);
    setIssue(problem);
    if (problem === null) save.mutate();
  };

  const isPeriod = form.kind === "period";
  const busy = save.isPending || remove.isPending;
  const proposing = propose !== undefined;
  const visibilities: { value: Visibility; label: string }[] = [
    { value: "private", label: "Moi seul" },
    { value: "family", label: "La famille" },
    { value: "custom", label: "Des proches choisis…" },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        aria-label="Fermer sans enregistrer"
        className="absolute inset-0 hidden cursor-default bg-tl-veil md:block"
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />
      <div
        aria-labelledby={`${id}-titre`}
        aria-modal="true"
        className="relative flex h-full w-full flex-col bg-background text-foreground md:w-[440px] md:border-l md:border-border"
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
        role="dialog"
        style={{
          backgroundImage: "radial-gradient(ellipse at 50% 30%, #1A2440 0%, #0D1222 75%)",
        }}
      >
        <header className="flex items-center justify-between border-b border-[rgba(243,238,227,0.14)] py-2 pr-2 pl-4">
          <h2 className="m-0 text-[17px] font-semibold" id={`${id}-titre`}>
            {heading ??
              (proposing
                ? "Proposer un souvenir"
                : entry === null
                  ? "Nouveau point"
                  : "Modifier le point")}
          </h2>
          <button
            aria-label="Fermer"
            className="inline-flex size-11 items-center justify-center rounded-full text-[#C9D0DE]"
            onClick={onClose}
            type="button"
          >
            <svg
              aria-hidden="true"
              fill="none"
              height="20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="20"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <form className="flex min-h-0 flex-1 flex-col" noValidate onSubmit={submit}>
          <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto p-4">
            <fieldset className="m-0 min-w-0 border-0 p-0">
              <legend className="sr-only">Type de point</legend>
              <div className="grid grid-cols-2 overflow-hidden rounded-[10px] border border-input">
                {(
                  [
                    ["point", "Un moment"],
                    ["period", "Une période"],
                  ] as const
                ).map(([kind, label]) => (
                  <button
                    aria-pressed={form.kind === kind}
                    className={`min-h-11 text-sm ${form.kind === kind ? "bg-primary font-semibold text-primary-foreground" : ""}`}
                    key={kind}
                    onClick={() => set("kind", kind)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="flex flex-col gap-1.5">
              <label className={LABEL} htmlFor={`${id}-quoi`}>
                Quoi ?
              </label>
              <input
                className={FIELD}
                id={`${id}-quoi`}
                maxLength={LIMITS.title}
                onChange={(event) => set("title", event.target.value)}
                ref={titleRef}
                required
                value={form.title}
              />
            </div>

            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className={`${LABEL} mb-2 p-0`}>Piste</legend>
              <div className="flex flex-wrap gap-2">
                {tracks.map((track) => {
                  const on = form.trackId === track.id;
                  return (
                    <button
                      aria-pressed={on}
                      className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-sm ${on ? "border-2 border-foreground bg-[rgba(243,238,227,0.08)]" : "border border-input"}`}
                      key={track.id}
                      onClick={() => set("trackId", track.id)}
                      type="button"
                    >
                      <span
                        aria-hidden="true"
                        className="size-2.5 rounded-full"
                        style={{ background: track.color }}
                      />
                      {track.name}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="flex flex-col gap-1.5">
              <label className={LABEL} htmlFor={`${id}-quand`}>
                {isPeriod ? "Depuis quand ?" : "Quand ?"}
              </label>
              <input
                aria-describedby={`${id}-quand-aide ${id}-quand-compris`}
                className={FIELD}
                id={`${id}-quand`}
                maxLength={LIMITS.raw}
                onChange={(event) => set("startRaw", event.target.value)}
                ref={whenRef}
                value={form.startRaw}
              />
              <p className="m-0 text-xs text-muted-foreground" id={`${id}-quand-aide`}>
                Comme ça te vient : « 2014 », « mars 2014 », « été 2003 », « vers 15 ans », « en 4e
                », « au lycée ». Vide, le point attend dans « à dater ».
              </p>
              {proposing ? null : <DatePreview id={`${id}-quand-compris`} raw={form.startRaw} />}
            </div>

            {isPeriod ? (
              <div className="flex flex-col gap-1.5">
                <label className={LABEL} htmlFor={`${id}-fin`}>
                  Jusqu'à quand ?
                </label>
                <input
                  aria-describedby={`${id}-fin-compris`}
                  className={`${FIELD} disabled:opacity-50`}
                  disabled={form.ongoing}
                  id={`${id}-fin`}
                  maxLength={LIMITS.raw}
                  onChange={(event) => set("endRaw", event.target.value)}
                  value={form.ongoing ? "" : form.endRaw}
                />
                <label className="flex min-h-11 items-center gap-2.5 text-[15px]">
                  <input
                    checked={form.ongoing}
                    className="size-5 accent-[#F3EEE3]"
                    onChange={(event) => set("ongoing", event.target.checked)}
                    type="checkbox"
                  />
                  En cours
                </label>
                {form.ongoing || proposing ? null : (
                  <DatePreview id={`${id}-fin-compris`} raw={form.endRaw} />
                )}
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label className={LABEL} htmlFor={`${id}-note`}>
                Une note <span className="font-normal text-muted-foreground">(facultatif)</span>
              </label>
              <textarea
                className={`${FIELD} resize-none py-2.5 font-[inherit]`}
                id={`${id}-note`}
                maxLength={LIMITS.note}
                onChange={(event) => set("note", event.target.value)}
                placeholder="Où, avec qui, un détail"
                rows={2}
                value={form.note}
              />
            </div>

            {proposing ? null : (
              <fieldset className="m-0 border-0 p-0">
                <legend className={`${LABEL} mb-2 p-0`}>Qui le voit ?</legend>
                <div className="flex flex-col gap-1">
                  {visibilities.map((option) => (
                    <label
                      className="flex min-h-11 items-center gap-2.5 text-[15px]"
                      key={option.value}
                    >
                      <input
                        checked={form.visibility === option.value}
                        className="size-5 accent-[#F3EEE3]"
                        name={`${id}-vis`}
                        onChange={() => set("visibility", option.value)}
                        type="radio"
                        value={option.value}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                {form.visibility === "custom" ? (
                  <ViewerPicker
                    onChange={(viewers) => set("viewers", viewers)}
                    value={form.viewers}
                  />
                ) : null}
              </fieldset>
            )}

            {entry !== null && submitBody === undefined && !proposing ? (
              <div className="flex flex-col gap-2 border-t border-[rgba(243,238,227,0.14)] pt-4">
                {confirmDelete ? (
                  <>
                    <p className="m-0 text-sm">Supprimer « {entry.title} » de ta frise ?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        className="min-h-11 rounded-[10px] border border-destructive text-sm text-destructive"
                        disabled={busy}
                        onClick={() => remove.mutate()}
                        type="button"
                      >
                        Oui, supprimer
                      </button>
                      <button
                        className="min-h-11 rounded-[10px] border border-input text-sm"
                        onClick={() => setConfirmDelete(false)}
                        type="button"
                      >
                        Annuler
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    className="min-h-11 self-start text-sm text-destructive underline"
                    onClick={() => setConfirmDelete(true)}
                    type="button"
                  >
                    Supprimer ce point
                  </button>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-2 border-t border-[rgba(243,238,227,0.14)] px-4 pt-3 pb-5">
            {issue !== null ? (
              <p className="m-0 text-sm text-destructive" role="alert">
                {issue}
              </p>
            ) : null}
            {serverError !== null ? (
              <div className="flex flex-col items-start text-sm text-destructive" role="alert">
                <span>{serverError.message}</span>
                {serverError.code === "needs_birth_date" && !proposing ? (
                  <Link
                    className="inline-flex min-h-11 items-center text-tl-accent underline"
                    to="/reglages"
                  >
                    Renseigner ma date de naissance
                  </Link>
                ) : null}
              </div>
            ) : null}
            <button
              className="min-h-12 rounded-[10px] bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
              disabled={busy}
              type="submit"
            >
              {save.isPending
                ? "Enregistrement..."
                : proposing
                  ? "Proposer"
                  : entry === null || submitBody !== undefined
                    ? "Ajouter à ma frise"
                    : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ROLE_LABELS = { family: "Famille", close: "Proche choisi" } as const;

/**
 * Le choix des proches d'un point « Des proches choisis » : une case par proche actif (le
 * proprietaire seul ouvre cette feuille avec la visibilite). La liste vient de `/proches`.
 */
function ViewerPicker({
  value,
  onChange,
}: {
  value: readonly number[];
  onChange: (viewers: number[]) => void;
}) {
  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers, retry: false });
  if (members.isPending) {
    return (
      <p className="m-0 mt-1 text-sm text-muted-foreground" role="status">
        Chargement de tes proches...
      </p>
    );
  }
  if (members.isError) {
    return (
      <p className="m-0 mt-1 text-sm text-destructive" role="alert">
        Tes proches ne répondent pas pour le moment.
      </p>
    );
  }
  const active = members.data.members.filter((member) => member.revokedAt === null);
  if (active.length === 0) {
    return (
      <p className="m-0 mt-1 text-sm text-muted-foreground">
        Tu n'as encore invité personne.{" "}
        <Link className="text-tl-accent underline" to="/proches">
          Inviter un proche
        </Link>
      </p>
    );
  }
  return (
    <fieldset className="m-0 mt-2 flex flex-col gap-1 border-0 border-l-2 border-input p-0 pl-3">
      <legend className="sr-only">Les proches qui le voient</legend>
      {active.map((member) => (
        <label className="flex min-h-11 items-center gap-2.5 text-[15px]" key={member.id}>
          <input
            checked={value.includes(member.id)}
            className="size-5 accent-[#F3EEE3]"
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...value, member.id]
                  : value.filter((viewer) => viewer !== member.id),
              )
            }
            type="checkbox"
          />
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{member.email}</span>
            <span className="text-xs text-muted-foreground">{ROLE_LABELS[member.role]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
